import React, { useEffect, useState } from "react";
import { FileSignature, Plus, Pencil, Trash2, FileDown, Search } from "lucide-react";
import { api, API, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useEmployees, PageHeader } from "@/components/CrudModule";
import { Badge2 } from "@/components/ComplianceBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { toast } from "sonner";

const OBJ = "Tem este documento a finalidade de esclarecer ao funcionário sobre os riscos de acidentes ou doenças existentes na sua função, bem como sobre as medidas preventivas necessárias para reduzir ou neutralizar a ação dos agentes agressivos. É dever do funcionário seguir rigorosamente estes procedimentos, conforme disposto na NR-1 da Portaria MTE nº 765, de 15 de maio de 2025.";
const EMPTY = { os_number: "", date: "", resp: "", employee_id: "", employee_name: "", setor: "", funcao: "", objetivos: OBJ, atividade: "", riscos: "", agentes: "", medidas: "", epis: "", treinamentos: "" };

export default function ServiceOrders() {
  const { canEdit, canManage } = useAuth();
  const employees = useEmployees();
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [del, setDel] = useState(null);

  const load = () => api.get("/service-orders").then(({ data }) => setRows(data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const setF = (k, v) => setForm((p) => ({ ...p, [k]: v }));
  const onEmployee = (id) => {
    const e = employees.find((x) => x.id === id);
    setForm((p) => ({ ...p, employee_id: id, employee_name: e?.name || "", setor: e?.department || p.setor, funcao: e?.role || p.funcao }));
  };
  const openNew = () => { setEditing(null); setForm({ ...EMPTY, date: new Date().toISOString().slice(0, 10) }); setOpen(true); };
  const openEdit = (r) => { setEditing(r); setForm({ ...EMPTY, ...r }); setOpen(true); };
  const save = async () => {
    if (!form.os_number || !form.date) { toast.error("Informe o número e a data da O.S."); return; }
    try {
      if (editing) await api.put(`/service-orders/${editing.id}`, form);
      else await api.post("/service-orders", form);
      toast.success("O.S. salva"); setOpen(false); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const remove = async () => { try { await api.delete(`/service-orders/${del.id}`); toast.success("Excluída"); setDel(null); load(); } catch { toast.error("Falha"); } };

  return (
    <div data-testid="os-page">
      <PageHeader icon={FileSignature} title="Ordem de Serviço (NR-01)" subtitle="Emissão de O.S. de Segurança e Saúde no Trabalho por colaborador">
        {canEdit && <Button size="sm" onClick={openNew} className="bg-[#0B2545] hover:bg-[#134074]" data-testid="os-new"><Plus className="mr-1.5 h-4 w-4" /> Nova O.S.</Button>}
      </PageHeader>

      <div className="overflow-hidden rounded-xl border border-[#E2E8F0] bg-white shadow-sm">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-left text-xs uppercase text-slate-500">
            <tr><th className="p-3">OS Nº</th><th className="p-3">Data</th><th className="p-3">Colaborador</th><th className="p-3">Setor</th><th className="p-3">Função</th><th className="p-3 text-right">Ações</th></tr>
          </thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan={6} className="p-6 text-center text-slate-400">Nenhuma O.S. emitida ainda.</td></tr>}
            {rows.map((r) => (
              <tr key={r.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`os-row-${r.os_number}`}>
                <td className="p-3 font-semibold text-[#0B2545]">{r.os_number}</td>
                <td className="p-3">{r.date}</td>
                <td className="p-3">{r.employee_name}</td>
                <td className="p-3">{r.setor}</td>
                <td className="p-3">{r.funcao}</td>
                <td className="p-3">
                  <div className="flex items-center justify-end gap-2">
                    <button onClick={() => window.open(`${API}/service-orders/${r.id}/pdf`, "_blank")} title="Gerar O.S. em PDF" className="text-[#134074] hover:text-[#0B2545]" data-testid={`os-pdf-${r.os_number}`}><FileDown className="h-4 w-4" /></button>
                    {canEdit && <button onClick={() => openEdit(r)} className="text-slate-500 hover:text-[#134074]"><Pencil className="h-4 w-4" /></button>}
                    {canManage && <button onClick={() => setDel(r)} className="text-rose-500 hover:text-rose-700"><Trash2 className="h-4 w-4" /></button>}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-[720px]" data-testid="os-dialog">
          <DialogHeader><DialogTitle className="text-[#0B2545]">{editing ? "Editar" : "Nova"} Ordem de Serviço</DialogTitle></DialogHeader>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            <div><Label className="mb-1 block text-xs font-semibold text-slate-600">OS Nº *</Label><Input value={form.os_number} onChange={(e) => setF("os_number", e.target.value)} placeholder="042/2026" data-testid="os-number" /></div>
            <div><Label className="mb-1 block text-xs font-semibold text-slate-600">Data *</Label><Input type="date" value={form.date} onChange={(e) => setF("date", e.target.value)} data-testid="os-date" /></div>
            <div><Label className="mb-1 block text-xs font-semibold text-slate-600">Responsável</Label><Input value={form.resp} onChange={(e) => setF("resp", e.target.value)} /></div>
            <div className="sm:col-span-3"><Label className="mb-1 block text-xs font-semibold text-slate-600">Colaborador executante</Label>
              <Select value={form.employee_id} onValueChange={onEmployee}><SelectTrigger data-testid="os-employee"><SelectValue placeholder="Selecione o colaborador" /></SelectTrigger>
                <SelectContent>{employees.map((e) => <SelectItem key={e.id} value={e.id}>{e.name} — {e.department}</SelectItem>)}</SelectContent></Select>
            </div>
            <div><Label className="mb-1 block text-xs font-semibold text-slate-600">Setor</Label><Input value={form.setor} onChange={(e) => setF("setor", e.target.value)} /></div>
            <div className="sm:col-span-2"><Label className="mb-1 block text-xs font-semibold text-slate-600">Função</Label><Input value={form.funcao} onChange={(e) => setF("funcao", e.target.value)} /></div>
          </div>
          {[["objetivos", "Objetivos"], ["atividade", "Atividade profissional"], ["riscos", "Riscos (um por linha)"], ["agentes", "Agentes (um por linha)"], ["medidas", "Medidas preventivas (uma por linha)"], ["epis", "EPIs obrigatórios"], ["treinamentos", "Treinamentos"]].map(([k, lbl]) => (
            <div key={k}><Label className="mb-1 block text-xs font-semibold text-slate-600">{lbl}</Label><Textarea rows={k === "atividade" || k === "objetivos" ? 4 : 2} value={form[k]} onChange={(e) => setF(k, e.target.value)} data-testid={`os-${k}`} /></div>
          ))}
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
            <Button onClick={save} className="bg-[#0B2545] hover:bg-[#134074]" data-testid="os-save">Salvar O.S.</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!del} onOpenChange={(o) => !o && setDel(null)}>
        <AlertDialogContent>
          <AlertDialogHeader><AlertDialogTitle>Excluir O.S. {del?.os_number}?</AlertDialogTitle><AlertDialogDescription>Esta ação não pode ser desfeita.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter><AlertDialogCancel>Cancelar</AlertDialogCancel><AlertDialogAction onClick={remove} className="bg-rose-600 hover:bg-rose-700">Excluir</AlertDialogAction></AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
